// AUTO-GENERATED by scripts/scaffold_canonical_breadcrumb.py.
// Source: Brand canon templates (Sprint 2026-06-11 PM-9).
// Customize the per-stage logic and count semantics for this studio
// after generation. See memory/COMPASS/01_technical/06_uxui/canonical-breadcrumb.md.

/**
 * Mission Architecture Alignment (Sprint 2026-06-03, rebuilt 2026-06-05 PM-7).
 *
 * Renders Row 1 of the canonical Sales Studio breadcrumb stack:
 *
 *   Row 1 → BRAND STUDIO: PILLARS
 *           Foundation → Protect → Strengthen → Brand Health → Steward
 *
 * Rows 2 and 3 are owned by FlowBreadcrumb (mission row) and
 * BrandStudioBreadcrumb (text path crumb) respectively. Each component
 * gates itself; together they produce the 3-row stack paco's
 * 2026-06-05 reference screenshots specify.
 *
 * --- Sprint 2026-06-11 PM-8 Phase 1 (paco) ---
 * On the Phase-1 canonical paths (see PHASE1_CANONICAL_PATHS below) this
 * component switches to the new 2-layer canonical view:
 *
 *   Row 1 → MISSION: SALES
 *           Foundation → Protect → Strengthen → Brand Health → Steward
 *
 *   Row 2 → <active pillar label>
 *           <sub-page chips from SALES_PILLAR_WORKFLOWS[pillar].items>
 *
 * The FlowBreadcrumb component self-suppresses on these paths so the
 * (wrong-mission) "Grow the Brand" row is no longer rendered.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, Target, UserPlus, Lightbulb, FileText, FileSignature, Presentation, Trophy } from "lucide-react";
import { api } from "../../lib/api";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  WIN_THE_CLIENT_MISSION,
  SALES_PILLAR_WORKFLOWS,
  detectSalesPillar,
  detectSalesStage,
  isSalesStudioPath,
} from "../../config/missionRegistry";
import SalesStageDrawer from "./SalesStageDrawer";
import { recordSalesLastVisited } from "../sales-studio/SalesStudioResumeChip";

// Sprint 2026-06-11 PM-9 Phase 3B (paco) — Sales-specific Lucide icons.
const ICON_BY_NAME = {
  Target, UserPlus, Lightbulb, FileText, FileSignature, Presentation, Trophy,
};

// Sprint 2026-06-11 PM-9 Phase 3C → 2026-06-12 PM-10.4 Phase 3D (paco):
// Sales canonical breadcrumb rolled out from the landing page (Phase 3C)
// to every Sales leaf route. Route inventory authoritative from App.js +
// SALES_PILLAR_WORKFLOWS in missionRegistry.js. See
// memory/COMPASS/01_technical/06_uxui/sales-mission-canon.md.
const PHASE1_CANONICAL_PATHS = [
  "/sales-studio",
  // Prospect children
  "/leads",
  "/contacts",
  "/briefs",
  // Concept
  "/concept-room",
  // Quote
  "/quotes",
  "/forms",
  "/intake-forms",                      // PM-10.7 — sidebar canonical for Forms
  // Proposal
  "/proposal-builder",
  "/proposal-analytics",                // PM-10.7 — UnifiedAnalyticsPage for proposals
  // Pitch (unified — One-Page Pitches AND Pitch Decks live in /one-page-pitches per
  // Sprint 2026-06-12 PM-10.5 audit; no standalone /pitch-decks route exists)
  "/one-page-pitches",
  // Win — Pipeline + Client Health + Sales Calendar + Sales Analytics (PM-10.7 paco override:
  // calendar+analytics are Sales-cluster destinations and get the canonical breadcrumb).
  "/kanban",
  "/administration/health",
  "/calendar",
  "/meetings",
  "/analytics",
];

/**
 * Sprint 2026-06-12 PM-10.6 — prefix-aware match so deep routes like
 * `/leads/abc`, `/quotes/builder/new`, `/proposal-builder/123` still render
 * the canonical 2-layer breadcrumb (paco: "in some Sales pages the
 * breadcrumb does not load at all" — root cause: `.includes()` was exact-only).
 */
function isPhase1CanonicalPath(pathname) {
  if (!pathname) return false;
  return PHASE1_CANONICAL_PATHS.some(
    (p) => pathname === p || pathname.startsWith(p + "/")
  );
}

// Drawer-enabled stages: any stage that has at least one child collection
// (i.e. all 6 Sales stages — the funnel is list-based end-to-end).
const DRAWER_PILLARS = new Set(["lead", "concept", "quote", "proposal", "pitch", "win"]);
const DRAWER_CHILDREN = new Set([
  "leads", "contacts", "briefs",
  "concept-room",
  "quotes", "forms", "intake-forms",
  "proposal-builder", "proposal-analytics",
  "one-page-pitches",
  "kanban", "administration/health",
  "calendar", "meetings", "analytics",
]);

function localized(obj, lang) {
  if (!obj) return "";
  return obj[lang] || obj.en || "";
}

/**
 * Sprint 2026-06-11 PM-9 Phase 1C — pulse heartbeat dot.
 *
 * Renders a tiny animated dot next to a label / badge when the underlying
 * section had activity in the last 24h. Visually surfaces "where the
 * action is today" without the user clicking around.
 *
 *   <PulseDot color="#6366F1" testid="pulse-foundation" />
 */
function PulseDot({ color, testid }) {
  return (
    <span
      className="relative inline-flex w-2 h-2 ml-1 shrink-0"
      data-testid={testid}
      aria-hidden="true"
    >
      <span
        className="absolute inset-0 rounded-full opacity-60 animate-ping"
        style={{ backgroundColor: color }}
      />
      <span
        className="relative inline-flex rounded-full w-2 h-2"
        style={{ backgroundColor: color }}
      />
    </span>
  );
}

export { isSalesStudioPath };

export default function SalesStudioPillarsBreadcrumb() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  // Sprint 2026-06-11 PM-9 Phase 1B — fetch count map for badges.
  // Sprint 2026-06-11 PM-9 Phase 1C — also includes recent_24h flags for
  // the pulse-heartbeat indicator.
  const [counts, setCounts] = useState({ pillars: {}, children: {}, recent_24h: { pillars: {}, children: {} } });
  const [openDrawer, setOpenDrawer] = useState(null); // "<scope>:<id>" or null
  const badgeRefs = useRef({});
  useEffect(() => {
    let cancelled = false;
    api.get("/sales-studio/mission-counts")
      .then((r) => {
        if (cancelled || !r?.data) return;
        setCounts({
          pillars: r.data.pillars || {},
          children: r.data.children || {},
          recent_24h: r.data.recent_24h || { pillars: {}, children: {} },
        });
      })
      .catch(() => { /* badges simply won't render if counts fail */ });
    return () => { cancelled = true; };
  }, [pathname]);

  // Sprint 2026-06-11 PM-9 Phase 1D — record last-visited leaf so the
  // Resume chip on the Sales Studio landing page can deeplink back.
  // We record on every path-change that resolves to a known pillar AND
  // a known sub-page id (avoids polluting storage on transient routes).
  useEffect(() => {
    const pillar = detectSalesPillar(pathname);
    if (!pillar) return;
    const workflow = SALES_PILLAR_WORKFLOWS[pillar];
    if (!workflow) return;
    const child = workflow.items.find(
      (c) => pathname === c.page || pathname.startsWith(c.page + "/")
    );
    if (!child) return;
    recordSalesLastVisited(pathname, pillar, child.id);
  }, [pathname]);

  if (!isSalesStudioPath(pathname)) return null;

  // CANON-FORMS-ENGINE (2026-07-08 · C7): Forms is an Engine; its mission bar must
  // reflect the mounting Studio, never Sales by default. Suppress the Sales bar on
  // Forms surfaces opened in a non-Sales Studio context (interim until Forms Engine sprint).
  const _formsStudio = new URLSearchParams(search || "").get("studio");
  const _isFormsSurface = pathname === "/forms" || pathname === "/intake-forms"
    || pathname.startsWith("/forms/") || pathname.startsWith("/intake-forms/");
  if (_isFormsSurface && _formsStudio && _formsStudio !== "sales") return null;
  // Sprint 2026-06-11 PM-9 Phase 3C (paco): on the Sales Studio landing
  // page we DO render the canonical breadcrumb (unlike Brand where the
  // landing hero already showed pillar cards). Sales has no hero pillar
  // cards, so the breadcrumb IS the primary stage navigation.

  const activeStageId = detectSalesStage(pathname);
  const missionAccent = WIN_THE_CLIENT_MISSION.color;
  const isPhase1Canonical = isPhase1CanonicalPath(pathname);
  const activePillarId = detectSalesPillar(pathname);
  const pillarWorkflow = activePillarId ? SALES_PILLAR_WORKFLOWS[activePillarId] : null;

  const pillars = WIN_THE_CLIENT_MISSION.stages.map((s) => ({
    id: s.id,
    label: localized(s.label, language),
    subtitle: localized(s.subtitle, language),
    page: s.page,
    color: s.color || missionAccent,
    Icon: ICON_BY_NAME[s.icon] || Layers,
  }));

  // The active pillar's color drives Layer-2 children styling (cohesion).
  const activePillar = pillars.find((p) => p.id === activeStageId);
  const childAccent = activePillar?.color || missionAccent;

  // Phase-1 header reads "MISSION: SALES".
  // Legacy header reads "Sales Studio: Pillars".
  const headerEyebrow = isPhase1Canonical ? t("Mission") : t("Sales Studio");
  const headerValue = isPhase1Canonical
    ? localized(WIN_THE_CLIENT_MISSION.label, language)
    : t("Pillars");

  return (
    <div className="w-full mb-3" data-testid="sales-studio-pillars-breadcrumb">
      {/* ── Row 1: Mission + Pillars strip ───────────────────────── */}
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--app-text-muted)]">
          {headerEyebrow}:
        </span>
        <span
          className="text-[10px] uppercase tracking-widest font-bold"
          style={{ color: missionAccent }}
          data-testid="sales-studio-pillars-breadcrumb-mission-label"
        >
          {headerValue}
        </span>
      </div>
      <div className="flex items-center gap-1 flex-wrap" data-testid="sales-studio-pillars-breadcrumb-strip">
        {pillars.map((it, i) => {
          const isCurrent = it.id === activeStageId;
          const accent = it.color;
          // Per-pillar tints (paco 2026-06-11 PM-9): every pill carries its
          // own color family. Current = vivid; non-current = subtle tint.
          const pillStyle = isCurrent
            ? { borderColor: `${accent}88`, backgroundColor: `${accent}1f`, color: accent, boxShadow: `0 1px 0 ${accent}22, 0 0 0 1px ${accent}33` }
            : { borderColor: `${accent}33`, backgroundColor: `${accent}0d`, color: "var(--app-text)" };
          const numStyle = isCurrent
            ? { backgroundColor: `${accent}40`, color: accent }
            : { backgroundColor: `${accent}22`, color: `${accent}cc` };
          return (
            <div key={it.id} className="flex items-center shrink-0">
              <button
                onClick={() => navigate(it.page)}
                className="group flex items-center gap-2 rounded-xl px-3 py-2 border transition-all cursor-pointer hover:scale-[1.02] hover:-translate-y-px"
                style={pillStyle}
                data-testid={`pillar-${it.id}`}
                data-current={isCurrent || undefined}
              >
                {/* Number bubble — kept per paco's directive. */}
                <span
                  className="inline-flex w-5 h-5 items-center justify-center rounded-full text-[10px] font-bold shrink-0"
                  style={numStyle}
                >
                  {i + 1}
                </span>
                {/* Sprint 2026-06-11 PM-9 Phase 1D — icon circle. */}
                <span
                  className="inline-flex w-7 h-7 items-center justify-center rounded-full shrink-0 transition-transform group-hover:rotate-3"
                  style={{
                    backgroundColor: isCurrent ? `${accent}28` : `${accent}14`,
                    color: accent,
                    boxShadow: isCurrent ? `inset 0 0 0 1px ${accent}40` : `inset 0 0 0 1px ${accent}20`,
                  }}
                  data-testid={`pillar-icon-${it.id}`}
                >
                  <it.Icon className="w-3.5 h-3.5" strokeWidth={2.25} />
                </span>
                {/* Label + subtitle stacked. */}
                <span className="flex flex-col items-start min-w-0 leading-tight pr-1">
                  <span className="text-[12px] font-semibold whitespace-nowrap">
                    {it.label}
                  </span>
                  <span
                    className="text-[10px] font-medium whitespace-nowrap opacity-75"
                    style={{ color: isCurrent ? `${accent}cc` : "var(--app-text-muted)" }}
                  >
                    {it.subtitle}
                  </span>
                </span>
                {/* Sprint 2026-06-11 PM-9 Phase 1C — pulse heartbeat. */}
                {counts.recent_24h?.pillars?.[it.id] && (
                  <PulseDot color={accent} testid={`pulse-pillar-${it.id}`} />
                )}
                {/* Sprint 2026-06-11 PM-9 Phase 1B — count badge.
                    Renders only when count > 0. For drawer-enabled pillars
                    (protect / strengthen / steward) it acts as a button
                    that toggles the popover. Brand Health renders a
                    non-interactive score badge (it's a single number). */}
                {(() => {
                  const cnt = counts.pillars?.[it.id];
                  if (cnt == null || cnt === 0) return null;
                  const drawable = DRAWER_PILLARS.has(it.id);
                  const badgeKey = `pillar:${it.id}`;
                  const badgeStyle = {
                    backgroundColor: isCurrent ? accent : `${accent}cc`,
                    color: "var(--app-bg)",
                  };
                  if (!drawable) {
                    // Brand Health → score-only, non-interactive.
                    return (
                      <span
                        className="ml-0.5 inline-flex items-center justify-center min-w-[20px] h-[18px] rounded-full px-1.5 text-[10px] font-bold"
                        style={badgeStyle}
                        data-testid={`pillar-count-${it.id}`}
                        data-count={cnt}
                      >
                        {cnt}
                      </span>
                    );
                  }
                  return (
                    <span
                      role="button"
                      tabIndex={0}
                      ref={(el) => { badgeRefs.current[badgeKey] = el; }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDrawer(openDrawer === badgeKey ? null : badgeKey);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          setOpenDrawer(openDrawer === badgeKey ? null : badgeKey);
                        }
                      }}
                      className="ml-0.5 inline-flex items-center justify-center min-w-[20px] h-[18px] rounded-full px-1.5 text-[10px] font-bold cursor-pointer hover:scale-110 transition-transform"
                      style={badgeStyle}
                      title={t("Click to see oldest at this stage")}
                      data-testid={`pillar-count-${it.id}`}
                      data-count={cnt}
                    >
                      {cnt}
                    </span>
                  );
                })()}
              </button>
              {openDrawer === `pillar:${it.id}` && DRAWER_PILLARS.has(it.id) && (
                <SalesStageDrawer
                  stage={it.id}
                  count={counts.pillars?.[it.id] || 0}
                  accent={accent}
                  anchorRef={{ current: badgeRefs.current[`pillar:${it.id}`] }}
                  onClose={() => setOpenDrawer(null)}
                />
              )}
              {i < pillars.length - 1 && (
                <ChevronRight className="w-3 h-3 text-[var(--app-text-muted)] opacity-40 shrink-0 mx-0.5" />
              )}
            </div>
          );
        })}
      </div>

      {/* ── Row 2: Active pillar's children (Phase-1 canonical paths only) ──
           Sprint 2026-06-11 PM-9 Phase 1C (paco): Row 2 is indented to
           visually convey hierarchy (mission → active pillar → children).
           A subtle elbow connector (└) drawn via borders ties it back to
           Row 1, so the row reads like an indented outline. */}
      {isPhase1Canonical && pillarWorkflow && (
        <div
          className="mt-2 pl-7 relative"
          data-testid="sales-studio-pillar-children-breadcrumb"
        >
          {/* Elbow connector (└) — vertical stem + horizontal arm, drawn
              with absolute-positioned borders so it scales with the row. */}
          <span
            aria-hidden="true"
            className="absolute top-0 left-2 w-3 h-[18px] border-l border-b rounded-bl-md pointer-events-none"
            style={{ borderColor: `${childAccent}55` }}
          />
          <div className="flex items-center gap-1.5 mb-1.5">
            <span
              className="text-[10px] uppercase tracking-widest font-bold"
              style={{ color: childAccent }}
            >
              {localized(pillarWorkflow.label, language)}
            </span>
          </div>
          <div
            className="flex items-center gap-1 flex-wrap"
            data-testid="sales-studio-pillar-children-strip"
          >
            {pillarWorkflow.items.map((child, ci) => {
              const isCurrentChild = pathname === child.page || pathname.startsWith(child.page + "/");
              const childPillStyle = isCurrentChild
                ? { borderColor: `${childAccent}88`, backgroundColor: `${childAccent}1f`, color: childAccent, boxShadow: `0 1px 0 ${childAccent}22, 0 0 0 1px ${childAccent}33` }
                : { borderColor: `${childAccent}26`, backgroundColor: `${childAccent}08`, color: "var(--app-text)" };
              const childNumStyle = isCurrentChild
                ? { backgroundColor: `${childAccent}40`, color: childAccent }
                : { backgroundColor: `${childAccent}1a`, color: `${childAccent}bb` };
              return (
                <div key={child.id} className="flex items-center shrink-0">
                  <button
                    onClick={() => navigate(child.page)}
                    className="flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-medium border transition-all cursor-pointer hover:scale-[1.02]"
                    style={childPillStyle}
                    data-testid={`pillar-child-${child.id}`}
                    data-current={isCurrentChild || undefined}
                  >
                    <span
                      className="inline-flex w-4 h-4 items-center justify-center rounded-full text-[9px] font-bold shrink-0"
                      style={childNumStyle}
                    >
                      {ci + 1}
                    </span>
                    {localized(child.label, language)}
                    {/* Sprint 2026-06-11 PM-9 Phase 1C — pulse heartbeat. */}
                    {counts.recent_24h?.children?.[child.id] && (
                      <PulseDot color={childAccent} testid={`pulse-child-${child.id}`} />
                    )}
                    {/* Sprint 2026-06-11 PM-9 Phase 1B — child count badge. */}
                    {(() => {
                      const cnt = counts.children?.[child.id];
                      if (cnt == null || cnt === 0) return null;
                      const drawable = DRAWER_CHILDREN.has(child.id);
                      const badgeKey = `child:${child.id}`;
                      const badgeStyle = {
                        backgroundColor: isCurrentChild ? childAccent : `${childAccent}cc`,
                        color: "var(--app-bg)",
                      };
                      if (!drawable) {
                        return (
                          <span
                            className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[16px] rounded-full px-1 text-[10px] font-bold"
                            style={badgeStyle}
                            data-testid={`pillar-child-count-${child.id}`}
                            data-count={cnt}
                          >
                            {cnt}
                          </span>
                        );
                      }
                      return (
                        <span
                          role="button"
                          tabIndex={0}
                          ref={(el) => { badgeRefs.current[badgeKey] = el; }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDrawer(openDrawer === badgeKey ? null : badgeKey);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              setOpenDrawer(openDrawer === badgeKey ? null : badgeKey);
                            }
                          }}
                          className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[16px] rounded-full px-1 text-[10px] font-bold cursor-pointer hover:scale-110 transition-transform"
                          style={badgeStyle}
                          title={t("Click to see oldest at this stage")}
                          data-testid={`pillar-child-count-${child.id}`}
                          data-count={cnt}
                        >
                          {cnt}
                        </span>
                      );
                    })()}
                  </button>
                  {openDrawer === `child:${child.id}` && DRAWER_CHILDREN.has(child.id) && (
                    <SalesStageDrawer
                      stage={child.id}
                      count={counts.children?.[child.id] || 0}
                      accent={childAccent}
                      anchorRef={{ current: badgeRefs.current[`child:${child.id}`] }}
                      onClose={() => setOpenDrawer(null)}
                    />
                  )}
                  {ci < pillarWorkflow.items.length - 1 && (
                    <ChevronRight className="w-3 h-3 text-[var(--app-text-muted)] opacity-40 shrink-0 mx-0.5" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
